import * as Api from '@ViewModels';
import { css, StyleSheet } from 'aphrodite';
import * as React from 'react';
import { Button } from '../../../components-v2/Button';
import {
	Dialog,
	DialogClose,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogPopup,
	DialogTitle,
} from '../../../components-v2/Dialog';
import { Dropdown, DropdownContent, DropdownItem, DropdownTrigger } from '../../../components-v2/Dropdown';
import { Popover, PopoverContent, PopoverTrigger } from '../../../components-v2/Popover';
import { THEME } from '../../../components-v2/Theme/theme';
import { shadowLg } from '../../../styles/shadows';
import { IDueDateValue } from '../utils/buckets';
import { PRIORITIES, PRIORITY_LABELS } from '../utils/priority';
import { AssigneeMenu } from './AssigneeMenu';
import { SnoozeMenu } from './SnoozeMenu';

interface IProps {
	count: number;
	users: Api.IUser[];
	onComplete: () => void;
	onReschedule: (value: IDueDateValue) => void;
	onReassign: (user: Api.IUser) => void;
	onSetPriority: (priority: Api.ActionItemPriority) => void;
	onDelete: () => void;
	onClear: () => void;
}

export const BulkActionBar: React.FC<IProps> = ({
	count,
	users,
	onComplete,
	onReschedule,
	onReassign,
	onSetPriority,
	onDelete,
	onClear,
}) => {
	const [rescheduleOpen, setRescheduleOpen] = React.useState(false);
	const [confirmDelete, setConfirmDelete] = React.useState(false);

	if (count === 0) {
		return null;
	}

	return (
		<div className={css(styleSheet.bar)} role='toolbar' aria-label='Bulk actions' data-testid='bulk-action-bar'>
			<span className={css(styleSheet.count)}>{count} selected</span>
			<Button size='sm' onClick={onComplete}>
				Complete
			</Button>
			<Popover open={rescheduleOpen} onOpenChange={setRescheduleOpen}>
				<PopoverTrigger asChild>
					<Button size='sm' variant='secondary'>
						Reschedule
					</Button>
				</PopoverTrigger>
				<PopoverContent align='start'>
					<SnoozeMenu
						current={{ dueDate: null, hasDueTime: false }}
						onSelect={value => {
							setRescheduleOpen(false);
							onReschedule(value);
						}}
					/>
				</PopoverContent>
			</Popover>
			<AssigneeMenu assignee={undefined} users={users} label='Reassign' onChange={onReassign} />
			<Dropdown>
				<DropdownTrigger
					render={props => (
						<Button {...props} size='sm' variant='secondary'>
							Priority
						</Button>
					)}
				/>
				<DropdownContent align='start'>
					{PRIORITIES.map(priority => (
						<DropdownItem key={priority} onClick={() => onSetPriority(priority)}>
							{PRIORITY_LABELS[priority]}
						</DropdownItem>
					))}
				</DropdownContent>
			</Dropdown>
			<Button size='sm' variant='destructive-secondary' onClick={() => setConfirmDelete(true)}>
				Delete
			</Button>
			<Button size='sm' variant='ghost' onClick={onClear}>
				Clear
			</Button>
			<Dialog open={confirmDelete} onOpenChange={setConfirmDelete}>
				<DialogPopup>
					<DialogHeader>
						<DialogTitle>Delete {count} action items?</DialogTitle>
						<DialogDescription>Once deleted they will be gone forever.</DialogDescription>
					</DialogHeader>
					<DialogFooter>
						<DialogClose
							render={props => (
								<Button variant='secondary' {...props}>
									Cancel
								</Button>
							)}
						/>
						<Button
							variant='destructive'
							onClick={() => {
								setConfirmDelete(false);
								onDelete();
							}}
						>
							Delete
						</Button>
					</DialogFooter>
				</DialogPopup>
			</Dialog>
		</div>
	);
};

const styleSheet = StyleSheet.create({
	bar: {
		position: 'sticky',
		bottom: THEME.spacing.md,
		zIndex: 5,
		display: 'flex',
		alignItems: 'center',
		gap: THEME.spacing.sm,
		flexWrap: 'wrap',
		alignSelf: 'center',
		padding: `${THEME.spacing.sm}px ${THEME.spacing.md}px`,
		borderRadius: THEME.common.borderRadius,
		border: THEME.common.border,
		backgroundColor: THEME.colors.background.light,
		boxShadow: shadowLg,
	},
	count: {
		fontSize: THEME.typography.fontSizes.sm,
		fontWeight: THEME.typography.fontWeights.bold,
		marginRight: THEME.spacing.sm,
	},
});
